import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import Button from '~components/Button/Button';
import { redirectToEhmp, sendHotjarEvent } from '~globals/common';
import { fireOpenModal, firePageViewEvent } from '~globals/tracking';
import { getLoggedInUser, getSession } from '~globals/user';
import useFeatureFlags from '~hooks/featureFlags/useFeatureFlags';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import DoctorIcon from '~images/inline/doctor.inline.svg';
import PencilIcon from '~images/inline/pencil.inline.svg';
import PillIcon from '~images/inline/pill.inline.svg';
import { toggleZipFormModal } from '~redux/Actions/DisplayActions';
import { updateZipAndCounty } from '~redux/Actions/GlobalActions';
import { updateSavedSession, updateUser, updateFavorites } from '~redux/Actions/UserActions';

type IConfig = {
    httpEndpoint?: string;
    env?: string;
    allid: string;
    isLoggedIn?: boolean;
    zipCode?: string;
    sessionCode?: string;
    withModal?: boolean;
    planYear?: number;
    onClose?: (
        data: IConfig & { planYear: number },
        context: unknown,
        element: { lastActiveElement: string | HTMLElement }
    ) => void;
    ui?:
        | {
              hideAllDisclaimerAndMmid?: boolean;
              summary: {
                  hideCreateAccountBanner: boolean;
                  hidePrintCta: boolean;
              };
              search: {
                  locationSearchLabel: string;
              };
          }
        | {
              hideAllMmid: boolean;
              summary: {
                  hideAddPharmacyBanner: boolean;
                  hideCreateAccountBanner: boolean;
                  hidePrintCta: boolean;
              };
          };
};

type Props = {
    btnWidth?: string;
    buttontype?: 'primary' | 'secondary' | 'link' | 'orange' | 'reskin' | 'rebrandAction';
    fontSize?: string;
    label?: string;
    trackingName: string;
    toolname: 'doctor' | 'drug';
    fontWeight?: string;
};

const MedicareTool = ({
    btnWidth = 'w-max max-w-80',
    buttontype = 'secondary',
    fontSize = 'text-base',
    fontWeight = 'font-bold',
    label,
    trackingName,
    toolname,
}: Props) => {
    const leadToken = useAppSelector((state) => state.global.leadToken);
    const ntmVisited = useAppSelector((state) => state.global.ntmVisited);
    const storeAllid = useAppSelector((state) => state.global.allianceId);
    const storeCounty = useAppSelector((state) => state.global.geoLocation.countyName);
    const storeZipcode = useAppSelector((state) => state.global.geoLocation.zip);
    const trackingParams = useAppSelector((state) => state.global.trackingParams);

    const isLoggedInGlobally = useAppSelector((state) => state.user.user.isLoggedIn);
    const {
        sessionCode: storeSessionCode,
        medications: storeMedications,
        providers: storeProviders,
    } = useAppSelector((state) => state.user.savedSession);

    const [hasLoaded, setHasLoaded] = useState(false);
    const [itemsCount, setItemsCount] = useState(0);
    const [trackingData, setTrackingData] = useState(`${trackingName}: add ${toolname}`);
    const [toolBeingOpened, setToolBeingOpened] = useState(null as EventTarget | null);
    const [didClickCta, setDidClickCta] = useState(false);
    const [didSignInFromTools, setDidSignInFromTools] = useState(false);
    const [isGuestSession, setIsGuestSession] = useState(false);

    const dispatch = useAppDispatch();
    const ccEnabled = useFeatureFlags().CustomerCenter();
    const enableZipModal = useFeatureFlags().ZipModal();

    enum TERMS {
        doctor = 'Doctors',
        drug = 'Rx Drugs',
    }

    const ICONS = {
        doctor: <DoctorIcon height="20px" width="20px" className="mr-2" />,
        drug: <PillIcon height="20px" width="20px" className="mr-2" />,
    };

    useEffect(() => {
        if (
            didClickCta &&
            storeSessionCode &&
            (isGuestSession || didSignInFromTools || isLoggedInGlobally) // prevents moving forward before we load logged-in user data
        ) {
            setDidClickCta(false);
            handleRedirect();
        }
    }, [
        didClickCta,
        storeSessionCode,
        isGuestSession,
        didSignInFromTools,
        isLoggedInGlobally,
        storeZipcode,
    ]);

    useEffect(() => {
        if (hasLoaded && toolBeingOpened) {
            void openToolWidget(toolBeingOpened);
        }
    }, [hasLoaded, toolBeingOpened]);

    useEffect(() => {
        if (toolname === 'drug') {
            const drugsCount = storeMedications?.length || 0;
            setItemsCount(drugsCount);
            if (drugsCount) {
                setTrackingData(`${trackingName}: add ${toolname}: ${drugsCount}drug`);
            } else {
                setTrackingData(`${trackingName}: add ${toolname}`);
            }
            return;
        }
        if (toolname === 'doctor') {
            const doctorsCount = storeProviders?.length || 0;
            setItemsCount(doctorsCount);
            if (doctorsCount) {
                setTrackingData(`${trackingName}: add ${toolname}: ${doctorsCount}doc`);
            } else {
                setTrackingData(`${trackingName}: add ${toolname}`);
            }
            return;
        }
    }, [storeMedications, storeProviders]);

    const handleClick = (event: React.MouseEvent) => {
        if (!hasLoaded) {
            loadTools().catch((e) => {
                console.log(e);
            });
        }
        setToolBeingOpened(event.target);
    };

    const openToolWidget = async (target: EventTarget) => {
        const instance = await configureInstance();
        instance.open(itemsCount ? 'SUMMARY' : 'SEARCH', target);
        resetFlags();
        sendHotjarEvent('Tools_click');
    };

    const resetFlags = () => {
        setToolBeingOpened(null);
        setDidClickCta(false);
        setIsGuestSession(false);
        setDidSignInFromTools(false);
    };

    const receiveSessionOnClose = (
        data: IConfig & { planYear: number },
        context: unknown,
        { lastActiveElement }: { lastActiveElement: string | HTMLElement }
    ) => {
        const isDismissed =
            lastActiveElement === 'Escape' ||
            (lastActiveElement as HTMLElement)?.dataset?.qa?.includes(':Close:');

        setDidClickCta(!isDismissed);
        getSession({ dispatch, updateSavedSession, updateZipAndCounty }, data).catch((e) => {
            console.error(e);
        });

        if (ccEnabled && data.isLoggedIn) {
            setIsGuestSession(false);
            if (!isLoggedInGlobally) {
                setDidSignInFromTools(true);
                sendHotjarEvent('CC_click');
                getLoggedInUser(storeSessionCode, {
                    dispatch,
                    updateUser,
                    updateFavorites,
                }).catch((e) => {
                    console.error(e);
                });
            } else {
                setDidSignInFromTools(false);
            }
        } else {
            setIsGuestSession(true);
        }
    };

    const handleRedirect = () => {
        if (enableZipModal && !storeZipcode) {
            triggerZipModal();
        } else {
            sendHotjarEvent('SeePlans_click');
            redirectToEhmp(
                {
                    allid: storeAllid,
                    zip: storeZipcode,
                    county: storeCounty,
                    leadToken: leadToken,
                    ppSaveSessionId: storeSessionCode,
                    ...trackingParams,
                    action: 'redirect_with_zip_code',
                    lastV1Action: 'quoteFromV1',
                    ...(ntmVisited.length > 0 && { n2m: 'true' }),
                },
                null,
                isLoggedInGlobally
            );
        }
    };

    const triggerZipModal = () => {
        dispatch(toggleZipFormModal(true));
        fireOpenModal();
        firePageViewEvent({
            sysEnv: { siteGroup: 'EHI' },
            page: {
                name: 'zip search',
                section: 'zip tool',
            },
            events: ['toolstart'],
        });
    };

    const loadTools = async () => {
        if (document.getElementById('medicare-tools') && window.MedicareTools) {
            setHasLoaded(true);
            return;
        }
        const baseUrl =
            process.env.NODE_ENV === 'production'
                ? '/medicare/medicare-tools/apps/MedicareTools'
                : '/medicare-tools/apps/MedicareTools';
        const loadJs = (): Promise<void> => {
            return new Promise((resolve) => {
                const s = document.createElement('script');
                s.type = 'text/javascript';
                s.defer = true;
                s.src = `${baseUrl}.umd.min.js`;
                s.id = 'medicare-tools';
                s.onload = () => {
                    resolve();
                };
                s.onerror = (e) => {
                    console.error(e);
                    resolve();
                };
                const x = document.getElementsByTagName('script')[0];
                (x.parentNode as ParentNode).insertBefore(s, x);
            });
        };
        const loadCss = (): Promise<void> => {
            return new Promise((resolve) => {
                const link = document.createElement('link');
                link.type = 'text/css';
                link.rel = 'stylesheet';
                link.href = `${baseUrl}.css`;
                link.onload = () => {
                    resolve();
                };
                link.onerror = (e) => {
                    console.error(e);
                    resolve();
                };
                document.head.appendChild(link);
            });
        };
        await Promise.all([loadCss(), loadJs()]);
        setHasLoaded(true);
    };

    const configureInstance = async () => {
        const widgetName = `${toolname}Tool`;
        const config: IConfig = {
            httpEndpoint: `${process.env.GATSBY_CC_GQL_URL}/graphql`,
            env: process.env.GATSBY_EH_ENV,
            zipCode: storeZipcode,
            allid: storeAllid,
            sessionCode: storeSessionCode,
            isLoggedIn: isLoggedInGlobally,
            withModal: true,
            onClose: receiveSessionOnClose,
        };
        if (toolname === 'doctor') {
            config.ui = {
                hideAllDisclaimerAndMmid: true,
                summary: {
                    hideCreateAccountBanner: !ccEnabled,
                    hidePrintCta: true,
                },
                search: {
                    locationSearchLabel: "Doctor's City or ZIP Code",
                },
            };
        }
        if (toolname === 'drug') {
            config.ui = {
                hideAllMmid: true,
                summary: {
                    hideAddPharmacyBanner: true,
                    hideCreateAccountBanner: !ccEnabled,
                    hidePrintCta: true,
                },
            };
        }
        return await window.MedicareTools[widgetName](config);
    };

    const renderBtnLabel = () => {
        if (itemsCount) {
            return (
                <span className="inline-flex items-center">
                    My {TERMS[toolname]} ({itemsCount}) <PencilIcon className="ml-1" />
                </span>
            );
        }
        switch (buttontype) {
            case 'reskin':
            case 'link':
                return (
                    <span
                        className={classNames(
                            'inline-flex items-center',
                            fontWeight ? fontWeight : 'font-bold'
                        )}
                    >
                        {label || `Add ${TERMS[toolname]}`}
                    </span>
                );
            case 'secondary':
            case 'primary':
            case 'orange':
            default:
                return (
                    <span className="inline-flex items-center">
                        {ICONS[toolname]} {label || `Add ${TERMS[toolname]}`}
                    </span>
                );
        }
    };

    return (
        <Button
            styletype={buttontype}
            onClick={handleClick}
            className={classNames('font-semibold px-1 min-w-48', btnWidth)}
            data-wa-link={trackingData}
            data-qa={
                buttontype === 'secondary'
                    ? `btn-${toolname}-tool`
                    : `btn-${buttontype}-${toolname}-tool`
            }
        >
            <span className={classNames('flex items-center', fontSize)}>{renderBtnLabel()}</span>
        </Button>
    );
};

export default MedicareTool;
